import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useMemo, type ReactNode } from "react";

import { api, ApiError } from "@/api/client";
import type { MeResponse, Membership, User } from "@/api/types";

import { tokenStorage } from "./tokenStorage";

export interface AuthContextValue {
  user: User | null;
  memberships: Membership[];
  isLoading: boolean;
  isError: boolean;
  isAdmin: boolean;
  hasRole: (teamId: string, role: "manager" | "creator" | "user") => boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<MeResponse | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.GET<MeResponse>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const login = useCallback(
    async (token: string) => {
      await tokenStorage.setToken(token);
      // Busca /auth/me com o token novo e seta o cache **diretamente**.
      // ``setQueryData`` e sincrono para subscribers do TanStack Query,
      // entao quando a Promise de ``login()`` resolve o ``useAuth().user``
      // ja esta populado na proxima render -- evita race com
      // ProtectedRoute redirecionando para /login enquanto ``user`` ainda
      // e null.
      try {
        const me = await api.GET<MeResponse>("/auth/me");
        queryClient.setQueryData(["me"], me);
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 404)
        ) {
          tokenStorage.clearToken();
          queryClient.setQueryData(["me"], null);
          throw err;
        }
        throw err;
      }
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    tokenStorage.clearToken();
    void api.POST("/auth/logout").catch(() => undefined);
    queryClient.setQueryData(["me"], null);
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<AuthContextValue>(() => {
    const user = data?.user ?? null;
    const memberships = data?.memberships ?? [];
    return {
      user,
      memberships,
      isLoading,
      isError,
      isAdmin: user?.is_admin ?? false,
      hasRole: (teamId, role) =>
        memberships.some((m) => m.team.id === teamId && m.role === role),
      login,
      logout,
      refresh,
    };
  }, [data, isLoading, isError, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
